import React, { useRef, useEffect, useCallback, useState } from "react";
import { Wrapper, Status } from "@googlemaps/react-wrapper";
import styled from "@emotion/styled";
import { getEnv } from "../../helpers/env";
import { googleMaps } from "../../adapter/googleMaps";
import { getPlaceData, Place } from "../../utils/map/address";
import "./googleAutocomplete.css";

import { TextField } from "../TextField";

interface SearchAddressProps {
  disabled?: boolean;
  onAddressSelect?: (address: string) => void;
  onPlaceFound?: (place: Place) => void;
  address?: string | null;
  isError?: boolean;
  helperText?: string;
}

const SearchAddress = ({
  disabled,
  onAddressSelect,
  onPlaceFound,
  address = null,
  isError,
  helperText,
  ...props
}: SearchAddressProps) => {
  const [isInitFlag, setIsInitFlag] = useState(false);
  const [status, setStatus] = useState<Status>(Status.LOADING);
  const autoCompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const env = getEnv();

  const onStatusUpdate = useCallback((status: Status) => setStatus(status), []);

  // on success
  useEffect(() => {
    const onPlaceChange = async () => {
      if (autoCompleteRef.current) {
        const place = await autoCompleteRef.current.getPlace();
        onPlaceFound(getPlaceData(place));
      }
    };

    // happens once because of isInitFlag
    if (status === Status.SUCCESS && inputRef.current && !isInitFlag) {
      autoCompleteRef.current = new google.maps.places.Autocomplete(
        inputRef.current,
        {
          componentRestrictions: { country: "us" },
          fields: ["address_components", "formatted_address", "geometry"],
          types: ["address"],
        }
      );

      autoCompleteRef.current.addListener("place_changed", onPlaceChange);
      setIsInitFlag(true);
    }
    if (inputRef.current) inputRef.current.value = address;
  }, [address, status, isInitFlag, onPlaceFound]);

  return (
    <>
      <HideAutoComplete />
      <Wrapper
        apiKey={googleMaps[env]}
        libraries={["places", "marker"]}
        callback={onStatusUpdate}
      >
        <HideAutoComplete />
        <StyledInputText
          {...props}
          ref={(e) => {
            inputRef.current = e;
          }}
          type="text"
          autoComplete="nope"
          placeholder="Enter Address"
          disabled={disabled}
          helperText={helperText}
          isError={isError}
          onBlur={(e) => {
            e.target.value && onAddressSelect(e.target.value);
          }}
        />
      </Wrapper>
    </>
  );
};

const StyledInputText = styled(TextField)`
  width: 100%;
  box-sizing: border-box;
  background: #fffefc;
  border: none;
  border-radius: 8px;
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;

  &[disabled] {
    background: #ededed;
  }
  .sr-text-input {
    width: 100%;
    height: 100%;
    max-width: 100%;
    padding: 12px 16px;
  }
`;

const HideAutoComplete = styled.input`
  display: none;
`;

export { SearchAddress };
export type { SearchAddressProps };
